import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Globe, Search, RefreshCw, ExternalLink, Eye, Activity, Rocket, Shield, CircleDot, XCircle, Loader2 } from "lucide-react";
import api, { notifyError } from "@/lib/api";
import PlatformLayout from "@/components/PlatformLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { toast } from "sonner";
import { WebsiteStatusBadge, HealthBadge, SslBadge } from "./websites/WebsiteBadges";

const TRIGGER = "bg-white/5 border-white/10 text-white hover:bg-white/10";
const CONTENT = "z-[110] bg-[#0F0F17] border-white/10 text-white";
const ITEM = "text-white focus:bg-white/10 focus:text-white";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

function SummaryCard({ label, value, tone = "neutral", icon: Icon, testid }) {
  const tones = {
    neutral: "text-white/70", success: "text-emerald-300", danger: "text-rose-300",
    warning: "text-amber-300", info: "text-sky-300", ssl: "text-cyan-300",
  };
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-1.5">
        {Icon ? <Icon size={14} className={tones[tone]} /> : null}
        <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold">{label}</div>
      </div>
      <div className={`text-2xl font-black font-display ${tones[tone]}`}>{value}</div>
    </div>
  );
}

export default function PlatformWebsites() {
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [health, setHealth] = useState("");
  const [ssl, setSsl] = useState("");
  const [plan, setPlan] = useState("");
  const [refreshingId, setRefreshingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      if (health) params.health = health;
      if (ssl) params.ssl = ssl;
      if (plan) params.plan = plan;
      const r = await api.get("/platform/websites", { params });
      setItems(r.data.items || []);
      setSummary(r.data.summary || null);
    } catch (e) { notifyError(e); } finally { setLoading(false); }
  }, [q, status, health, ssl, plan]);

  useEffect(() => { load(); }, [load]);

  const refreshOne = async (id) => {
    setRefreshingId(id);
    try {
      await api.post(`/platform/websites/${id}/refresh`, {});
      toast.success("Website read model di-refresh.");
      load();
    } catch (e) { notifyError(e, "Refresh gagal"); }
    finally { setRefreshingId(null); }
  };

  return (
    <PlatformLayout title="Website Control Center">
      <div data-testid="platform-websites-page">
        {/* Summary cards */}
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
          <SummaryCard label="Total" value={summary?.total ?? 0} icon={Globe} testid="ws-summary-total" />
          <SummaryCard label="Online" value={summary?.online ?? 0} tone="success" icon={Rocket} testid="ws-summary-online" />
          <SummaryCard label="Offline" value={summary?.offline ?? 0} tone="danger" icon={XCircle} testid="ws-summary-offline" />
          <SummaryCard label="Deploying" value={summary?.deploying ?? 0} tone="info" icon={Loader2} testid="ws-summary-deploying" />
          <SummaryCard label="Failed" value={summary?.failed ?? 0} tone="danger" icon={XCircle} testid="ws-summary-failed" />
          <SummaryCard label="Unknown" value={summary?.unknown ?? 0} tone="neutral" icon={CircleDot} testid="ws-summary-unknown" />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-5">
          <SummaryCard label="Healthy" value={summary?.healthy ?? 0} tone="success" icon={Activity} testid="ws-summary-healthy" />
          <SummaryCard label="Warning" value={summary?.warning ?? 0} tone="warning" icon={Activity} testid="ws-summary-warning" />
          <SummaryCard label="Critical" value={summary?.critical ?? 0} tone="danger" icon={Activity} testid="ws-summary-critical" />
          <SummaryCard label="SSL Valid" value={summary?.ssl_valid ?? 0} tone="ssl" icon={Shield} testid="ws-summary-ssl-valid" />
          <SummaryCard label="SSL Expiring" value={summary?.ssl_expiring ?? 0} tone="warning" icon={Shield} testid="ws-summary-ssl-expiring" />
          <SummaryCard label="SSL Expired" value={summary?.ssl_expired ?? 0} tone="danger" icon={Shield} testid="ws-summary-ssl-expired" />
        </div>

        {/* Filters */}
        <div className="mb-4 flex items-center gap-2 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari tenant / domain / slug…"
              className="bg-white/5 border-white/10 text-white placeholder:text-white/30 pl-9" data-testid="ws-search" />
          </div>
          <Select value={status || "ALL"} onValueChange={(v) => setStatus(v === "ALL" ? "" : v)}>
            <SelectTrigger className={`w-40 ${TRIGGER}`} data-testid="ws-filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className={CONTENT}>
              {["ALL", "ONLINE", "OFFLINE", "DEPLOYING", "MAINTENANCE", "FAILED", "SUSPENDED", "UNKNOWN"].map((v) => (
                <SelectItem key={v} value={v} className={ITEM}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={health || "ALL"} onValueChange={(v) => setHealth(v === "ALL" ? "" : v)}>
            <SelectTrigger className={`w-36 ${TRIGGER}`} data-testid="ws-filter-health"><SelectValue placeholder="Health" /></SelectTrigger>
            <SelectContent className={CONTENT}>
              {["ALL", "HEALTHY", "WARNING", "CRITICAL", "UNKNOWN"].map((v) => (
                <SelectItem key={v} value={v} className={ITEM}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={ssl || "ALL"} onValueChange={(v) => setSsl(v === "ALL" ? "" : v)}>
            <SelectTrigger className={`w-40 ${TRIGGER}`} data-testid="ws-filter-ssl"><SelectValue placeholder="SSL" /></SelectTrigger>
            <SelectContent className={CONTENT}>
              {["ALL", "VALID", "EXPIRING_SOON", "EXPIRED", "NOT_CONFIGURED"].map((v) => (
                <SelectItem key={v} value={v} className={ITEM}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={plan || "ALL"} onValueChange={(v) => setPlan(v === "ALL" ? "" : v)}>
            <SelectTrigger className={`w-32 ${TRIGGER}`} data-testid="ws-filter-plan"><SelectValue placeholder="Plan" /></SelectTrigger>
            <SelectContent className={CONTENT}>
              {["ALL", "STARTER", "PRO", "ENTERPRISE"].map((v) => (
                <SelectItem key={v} value={v} className={ITEM}>{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="ghost" onClick={load} className="text-white/60 hover:text-white hover:bg-white/10" data-testid="ws-reload">
            <RefreshCw size={14} className="mr-1" /> Reload
          </Button>
        </div>

        {/* Table */}
        {loading ? (
          <div className="text-white/40 text-sm p-6 rounded-2xl border border-white/10 text-center">Memuat…</div>
        ) : items.length === 0 ? (
          <div className="text-white/40 text-sm p-8 rounded-2xl border border-white/10 text-center">Belum ada website / tidak ada hasil filter.</div>
        ) : (
          <div className="rounded-2xl border border-white/10 overflow-hidden">
            <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-white/5 text-[10px] uppercase tracking-wider text-white/40 font-bold">
              <div className="col-span-3">Website / Tenant</div>
              <div className="col-span-2">Domain</div>
              <div className="col-span-1">Plan</div>
              <div className="col-span-2">Status</div>
              <div className="col-span-1">Health</div>
              <div className="col-span-1">SSL</div>
              <div className="col-span-1">Last Deploy</div>
              <div className="col-span-1 text-right">Actions</div>
            </div>
            {items.map((w) => (
              <div key={w.website_id} className="grid grid-cols-1 md:grid-cols-12 gap-2 px-4 py-3 border-t border-white/5 items-center"
                data-testid={`ws-row-${w.website_id}`}>
                <div className="col-span-3 min-w-0">
                  <Link to={`/platform/websites/${w.website_id}`} className="text-sm font-bold text-white hover:text-emerald-300 truncate block" data-testid={`ws-name-${w.website_id}`}>
                    {w.tenant_name}
                  </Link>
                  <div className="text-[10px] text-white/40 font-mono">{w.tenant_code} · {w.tenant_slug}</div>
                </div>
                <div className="col-span-2 min-w-0">
                  <div className="text-xs font-mono text-white truncate">{w.domain || "—"}</div>
                  <div className="text-[10px] text-white/40">{w.environment}</div>
                </div>
                <div className="col-span-1"><span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-purple-500/15 text-purple-300 border border-purple-500/30">{w.plan}</span></div>
                <div className="col-span-2"><WebsiteStatusBadge status={w.website_status} size="sm" /></div>
                <div className="col-span-1"><HealthBadge status={w.health_status} size="sm" /></div>
                <div className="col-span-1"><SslBadge status={w.ssl_status} size="sm" /></div>
                <div className="col-span-1 text-[10px] text-white/40">{fmt(w.last_deployment)}</div>
                <div className="col-span-1 flex gap-1 justify-end">
                  <button onClick={() => refreshOne(w.website_id)} disabled={refreshingId === w.website_id}
                    className="w-7 h-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10 disabled:opacity-50"
                    title="Refresh" data-testid={`ws-refresh-${w.website_id}`}>
                    <RefreshCw size={12} className={refreshingId === w.website_id ? "animate-spin" : ""} />
                  </button>
                  <Link to={`/platform/websites/${w.website_id}`} className="w-7 h-7 rounded-lg grid place-items-center text-white/60 hover:text-white hover:bg-white/10"
                    title="Detail" data-testid={`ws-view-${w.website_id}`}>
                    <Eye size={12} />
                  </Link>
                  {w.domain && (
                    <a href={`https://${w.domain}`} target="_blank" rel="noreferrer"
                      className="w-7 h-7 rounded-lg grid place-items-center text-emerald-400 hover:bg-emerald-500/10"
                      title="Open Website" data-testid={`ws-open-${w.website_id}`}>
                      <ExternalLink size={12} />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </PlatformLayout>
  );
}
